import {
  ArrowLeftOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DeploymentAccessBadge } from '../components/DeploymentAccessBadge';
import { AuthBrand, usePublicBranding } from '../components/AuthBrand';

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

export function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState<string>();
  const { siteName, logoUrl, passwordMinLength } = usePublicBranding();
  const [token] = useState(() => {
    const source = window.location.hash.slice(1) || window.location.search;
    const params = new URLSearchParams(source.replace(/^\?/, ''));
    return params.get('token') ?? '';
  });
  useEffect(() => {
    if (token) {
      window.history.replaceState(window.history.state, '', '/reset-password');
    }
  }, [token]);

  async function submit(values: ResetPasswordForm) {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          token,
          newPassword: values.newPassword,
        }),
      });
      if (!response.ok) {
        throw new Error(
          response.status === 422
            ? t('resetPassword.rule', { min: passwordMinLength })
            : response.status === 429
              ? t('resetPassword.rateLimited')
              : t('resetPassword.invalid'),
        );
      }
      setError(undefined);
      setCompleted(true);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : t('resetPassword.invalid'),
      );
      setCompleted(false);
    } finally {
      setLoading(false);
    }
  }

  const hasToken = Boolean(token);

  return (
    <main className="login-page">
      <header className="login-header">
        <AuthBrand siteName={siteName} logoUrl={logoUrl} />
        <Select
          aria-label={t('common.language')}
          value={i18n.resolvedLanguage ?? 'zh-CN'}
          onChange={(value) => void i18n.changeLanguage(value)}
          options={[
            { value: 'zh-CN', label: '简体中文' },
            { value: 'en-US', label: 'English' },
          ]}
        />
      </header>
      <section className="login-stage" aria-labelledby="reset-password-title">
        <div className="login-intro">
          <span className="eyebrow">ACCOUNT RECOVERY</span>
          <Typography.Title id="reset-password-title" level={1}>
            {t('resetPassword.title')}
          </Typography.Title>
          <Typography.Paragraph>
            {t('resetPassword.subtitle', { siteName })}
          </Typography.Paragraph>
        </div>
        <Card className="login-card" variant="borderless">
          {completed ? (
            <div className="auth-success">
              <Alert
                type="success"
                showIcon
                message={t('resetPassword.successTitle')}
                description={t('resetPassword.successDescription', { siteName })}
              />
              <Button
                type="primary"
                block
                onClick={() => void navigate('/login')}
              >
                {t('resetPassword.backToLogin')}
              </Button>
            </div>
          ) : !hasToken ? (
            <div className="auth-success">
              <Alert
                type="error"
                showIcon
                message={t('resetPassword.invalid')}
              />
              <Link to="/login">
                <ArrowLeftOutlined /> {t('resetPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            <Form<ResetPasswordForm>
              layout="vertical"
              size="large"
              onFinish={(values) => void submit(values)}
            >
              {error ? (
                <Alert
                  className="auth-form-error"
                  type="error"
                  showIcon
                  message={error}
                />
              ) : null}
              <Form.Item
                label={t('resetPassword.newPassword')}
                name="newPassword"
                rules={[
                  { required: true },
                  {
                    min: passwordMinLength,
                    message: t('resetPassword.rule', {
                      min: passwordMinLength,
                    }),
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item
                label={t('resetPassword.confirmPassword')}
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      return !value || getFieldValue('newPassword') === value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(t('resetPassword.mismatch')),
                          );
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                {t('resetPassword.submit')}
              </Button>
              <div className="auth-back-link">
                <Link to="/login">
                  <ArrowLeftOutlined /> {t('resetPassword.backToLogin')}
                </Link>
              </div>
            </Form>
          )}
        </Card>
      </section>
      <footer className="login-footer">
        <Space split={<span>·</span>}>
          <DeploymentAccessBadge />
          <span>{t('login.version')}</span>
        </Space>
      </footer>
    </main>
  );
}
